import React, { useState, useEffect, useRef } from 'react'
import CheckBoxGroup from '../atoms/CheckBoxGroup'
import CustomDate from '../atoms/CustomDate'
import { useForm } from 'react-hook-form'
import CustomTable from '../organisms/CustomTable'
import Button from '../atoms/Button'
import { ReactComponent as Dropdown } from '../svgs/pdfdropdown.svg'
import { useSelector, useDispatch } from 'react-redux'
import { holdingReports } from '../../utils/API/holdingReports'
import toast from 'react-hot-toast'
import { transactionsReport } from '../../utils/API/transactionsReport'
import { projectsReport } from '../../utils/API/projectsReport'
import { retirementReports } from '../../utils/API/retirementReports'
import Badge from '../atoms/Badge'
import Pagination from '../atoms/Pagination'
import { Skeleton } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
const getLabel = {
  verified: 'Verified',
  requested: 'Requested',
  action_required: 'Action Required',
  under_verification: 'Under Verification',
  not_verified: 'Not Verified',
  sent_for_approval: 'Sent for Approval'
}

const optionsMapping = {
  projects: {
    heading: 'Projects',
    headers: [
      'Project Name',
      'Project ID',
      'Category',
      'No. of credits',
      'Location',
      'Registry',
      'Status'
    ],
    data: []
  },
  holdings: {
    heading: 'Holdings',
    headers: [
      'Project Name',
      'Project ID',
      'Category',
      'No. of credits',
      'Token Type',
      'Registry'
    ],
    data: []
  },
  transactions: {
    heading: 'Transactions',
    headers: [
      'Transaction ID',
      'Project ID',
      'No. of credits',
      'Total Amount',
      'Buyer Name',
      'Token Type'
    ],
    data: []
  },
  retirements: {
    heading: 'Retirements',
    headers: [
      'Project Name',
      'Project ID',
      'Category',
      'No. of credits',
      'Location',
      'Registry',
      'Status'
    ],
    data: []
  }
}

export default function Report () {
  const { userType } = useSelector(state => state.authReducer)
  const { register } = useForm()
  const [selectedOptions, setSelectedOptions] = useState([])
  const [displayOptions, setDisplayOptions] = useState([])
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [holdingsData, setHoldingData] = useState(null)
  const [transactionData, setTransactionData] = useState(null)
  const [projectsData, setProjectsData] = useState(null)
  const [retirementsData, setRetirementsData] = useState(null)
  const [isLoading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    projects: { totalItems: 0, totalPages: 0 },
    holdings: { totalItems: 0, totalPages: 0 },
    transactions: { totalItems: 0, totalPages: 0 },
    retirements: { totalItems: 0, totalPages: 0 }
  })
  const [currentPages, setCurrentPages] = useState({
    projects: 1,
    holdings: 1,
    transactions: 1,
    retirements: 1
  })
  const [loadingStates, setLoadingStates] = useState({
    projects: false,
    holdings: false,
    transactions: false,
    retirements: false
  })
  const dispatch = useDispatch()
  const pageSize = 5
  const prevPages = useRef(currentPages)

  const handleOptionChange = checkedValues => {
    setSelectedOptions(checkedValues)
  }

  useEffect(() => {
    for (const key of Object.keys(currentPages)) {
      if (currentPages[key] !== prevPages.current[key]) {
        if (key === 'projects') {
          fetchProjectsData(currentPages[key])
        } else if (key === 'holdings') {
          fetchHoldingsData(currentPages[key])
        } else if (key === 'transactions') {
          fetchTransactionsData(currentPages[key])
        } else if (key === 'retirements') {
          fetchRetirementsData(currentPages[key])
        }
      }
    }
    prevPages.current = { ...currentPages }
  }, [currentPages])

  const handleGenerateTables = async () => {
    try {
      setLoading(true)
      if (selectedOptions.includes('holdings')) {
        await fetchHoldingsData(currentPages.holdings)
      }
      if (selectedOptions.includes('transactions')) {
        await fetchTransactionsData(currentPages.transactions)
      }
      if (selectedOptions.includes('projects')) {
        await fetchProjectsData(currentPages.projects)
      }
      if (selectedOptions.includes('retirements')) {
        await fetchRetirementsData(currentPages.retirements)
      }
      setDisplayOptions(selectedOptions)
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const transformHoldingData = holdingReport => {
    return holdingReport.data.map(item => [
      item['Project Name'],
      item['Project ID'],
      item['Category'],
      item['No. of Credits'],
      item['Token Type'] || '',
      item['Registry']
    ])
  }

  const transformTransactionData = transactionReport => {
    return transactionReport.data.map(item => [
      item['Transaction ID'],
      item['Project ID'],
      item['No. of Credits'],
      item['Total Amount'],
      item['Buyer Name'],
      item['Token Type']
    ])
  }

  const transformProjectsData = projectsReport => {
    return projectsReport.paginatedData.map(item => [
      item['Project Name'],
      item['Project ID'],
      item['Category'],
      item['Number Of Credits'],
      item['Location'],
      item['Registry'].registryName,
      <Badge varient={item['Status']}>
        <div className='flex justify-center items-center w-36 text-xs h-5 '>
          {getLabel[item['Status']]}
        </div>
      </Badge>
    ])
  }

  const transformRetirementData = retirementsReport => {
    return retirementsReport.data.map(item => [
      item['Project Name'],
      item['Project ID'],
      item['Category'],
      item['No. of Credits'],
      item['Location'],
      item['Registry'],
      item['Action'] ? 'True' : 'False'
    ])
  }

  const fetchProjectsData = async page => {
    setLoadingStates(prev => ({ ...prev, projects: true }))
    try {
      if (selectedOptions.includes('projects')) {
        const projectsReportData = await projectsReport({
          fromDate: startDate,
          toDate: endDate,
          page,
          pageSize,
          dispatch
        })
        const transformedData = transformProjectsData(projectsReportData)
        setProjectsData(transformedData)
        setPagination(prev => ({
          ...prev,
          projects: {
            totalItems: projectsReportData.totalDocs,
            totalPages: projectsReportData.totalPages
          }
        }))
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoadingStates(prev => ({ ...prev, projects: false }))
    }
  }

  const fetchHoldingsData = async page => {
    setLoadingStates(prev => ({ ...prev, holdings: true }))
    try {
      if (selectedOptions.includes('holdings')) {
        const holdingReport = await holdingReports({
          fromDate: startDate,
          toDate: endDate,
          page,
          pageSize,
          dispatch
        })
        const transformedData = transformHoldingData(holdingReport)
        setHoldingData(transformedData)
        setPagination(prev => ({
          ...prev,
          holdings: {
            totalItems: holdingReport.totalDocs,
            totalPages: holdingReport.totalPages
          }
        }))
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoadingStates(prev => ({ ...prev, holdings: false }))
    }
  }

  const fetchTransactionsData = async page => {
    setLoadingStates(prev => ({ ...prev, transactions: true }))
    try {
      if (selectedOptions.includes('transactions')) {
        const transactionReport = await transactionsReport({
          fromDate: startDate,
          toDate: endDate,
          page,
          pageSize,
          dispatch
        })
        const transformedData = transformTransactionData(transactionReport)
        setTransactionData(transformedData)
        setPagination(prev => ({
          ...prev,
          transactions: {
            totalItems: transactionReport.totalDocs,
            totalPages: transactionReport.totalPages
          }
        }))
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoadingStates(prev => ({ ...prev, transactions: false }))
    }
  }

  const fetchRetirementsData = async page => {
    setLoadingStates(prev => ({ ...prev, retirements: true }))
    try {
      if (selectedOptions.includes('retirements')) {
        const retirementsReportData = await retirementReports({
          fromDate: startDate,
          toDate: endDate,
          page,
          pageSize,
          dispatch
        })
        const transformedData = transformRetirementData(retirementsReportData)
        setRetirementsData(transformedData)
        setPagination(prev => ({
          ...prev,
          retirements: {
            totalItems: retirementsReportData.totalDocs,
            totalPages: retirementsReportData.totalPages
          }
        }))
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoadingStates(prev => ({ ...prev, retirements: false }))
    }
  }

  const handleStartDateChange = date => {
    setStartDate(date)
  }

  const handleEndDateChange = date => {
    setEndDate(date)
  }

  const handlePageChange = (table, page) => {
    setCurrentPages(prev => ({ ...prev, [table]: page }))
  }

  const checkboxOptions =
    userType === 'seller'
      ? [
          { label: 'Projects', value: 'projects' },
          { label: 'Holdings', value: 'holdings' },
          { label: 'Transactions', value: 'transactions' }
        ]
      : [
          { label: 'Retirements', value: 'retirements' },
          { label: 'Holdings', value: 'holdings' },
          { label: 'Transactions', value: 'transactions' }
        ]
  // const handlePDFExport = () => {
  //   exportToPDF(tableData, selectedPage.headers, selectedPage.heading);
  // };

  // const handleExcelExport = () => {
  //   exportToExcel(tableData, selectedPage.headers, selectedPage.heading);
  // };

  const downloadExcel = () => {
    const selectedPage = optionsMapping[displayOptions[0]]
    const tableData = (() => {
      if (selectedPage.heading === 'Holdings' && holdingsData) {
        return holdingsData
      }
      if (selectedPage.heading === 'Transactions' && transactionData) {
        return transactionData
      }
      if (selectedPage.heading === 'Projects' && projectsData) {
        return projectsData
      }
      if (selectedPage.heading === 'Retirements' && retirementsData) {
        return retirementsData
      }
    })()

    const worksheet = XLSX.utils.json_to_sheet(
      tableData.map((row, index) =>
        selectedPage.headers.reduce(
          (acc, header, idx) => ({
            ...acc,
            [header]: row[idx]
          }),
          {}
        )
      )
    )
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report')
    XLSX.writeFile(workbook, 'report.xlsx')
  }

  const downloadPDF = () => {
    const selectedPage = optionsMapping[displayOptions[0]]
    const tableData = (() => {
      if (selectedPage.heading === 'Holdings' && holdingsData) {
        return holdingsData
      }
      if (selectedPage.heading === 'Transactions' && transactionData) {
        return transactionData
      }
      if (selectedPage.heading === 'Projects' && projectsData) {
        return projectsData
      }
      if (selectedPage.heading === 'Retirements' && retirementsData) {
        return retirementsData
      }
    })()

    const doc = new jsPDF()
    doc.text('Table Report', 14, 20)
    doc.autoTable({
      startY: 30,
      head: [selectedPage.headers],
      body: tableData
    })
    doc.save('report.pdf')
  }
  return (
    <div>
      <div className='flex w-full h-full'>
        <div className='w-full h-full bg-grey-100 py-5'>
          <p className='ml-5 my-7 text-gray-800 font-bold text-2xl'>Reports</p>
          <div className='mt-7 bg-white h-80 ml-5 shadow-md p-6 mx-4 rounded-lg justify-center'>
            <p className='text-gray-800 font-bold text-xl ml-5'>
              Please Select the report and time
            </p>
            <p className='text-gray-800 font-bold text-sm ml-5 mt-3'>Report</p>
            <div className='ml-5 mt-3'>
              <CheckBoxGroup
                options={checkboxOptions}
                onChange={handleOptionChange}
              />
            </div>
            <p className='mt-8 text-gray-800 font-bold text-sm ml-5'>Time</p>
            <div className='ml-5'>
              <CustomDate
                title='Select Date Range:'
                startDateLabel='From'
                endDateLabel='To'
                register={register}
                stateName={{ startDate: 'startfrom', endDate: 'endsto' }}
                labelRequired={false}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
              />
            </div>
            <div className='mt-4 text-center my-4 mx-2'>
              <Button
                color={'blue'}
                variant={'secondary'}
                className='items-center py-1.5 px-4 h-fit rounded-full text-white text-sm text-bold'
                onClick={handleGenerateTables}
                disabled={isLoading}
              >
                {isLoading ? <LoadingOutlined /> : 'Generate Reports'}
              </Button>
            </div>
          </div>
          {displayOptions.map(option => {
            const selectedPage = optionsMapping[option]
            const tableData = (() => {
              if (option === 'holdings' && holdingsData) {
                return holdingsData
              }
              if (option === 'transactions' && transactionData) {
                return transactionData
              }
              if (option === 'projects' && projectsData) {
                return projectsData
              }
              if (option === 'retirements' && retirementsData) {
                return retirementsData
              }
            })()
            return (
              <>
                {isLoading ? (
                  <div className='flex flex-col gap-y-4 p-4'>
                    <Skeleton.Button block={true} active={true} />
                    <Skeleton.Button block={true} active={true} />
                    <Skeleton.Button block={true} active={true} />
                    <Skeleton.Button block={true} active={true} />
                  </div>
                ) : (
                  <div
                    key={option}
                    className='mt-5 bg-white ml-5 shadow-formShadow p-6 mx-4 rounded-lg justify-center'
                  >
                    <div className='flex justify-between items-center'>
                      <p className='text-gray-800 text-xl font-bold ml-5'>
                        {selectedPage.heading}
                      </p>
                      <div className='flex gap-x-3'>
                        <Button
                          className='flex justify-center items-center py-1.5 px-1.5 h-fit rounded'
                          color={'blue'}
                          variant={'secondary'}
                          onClick={downloadPDF}
                        >
                          <Dropdown className='w-3 h-2' />
                          <span className='text-white text-[9px] font-bold mr-1'>
                            Pdf
                          </span>
                        </Button>

                        <Button
                          className='flex justify-center items-center py-1.5 px-1.5 h-fit rounded'
                          color={'blue'}
                          variant={'secondary'}
                          onClick={downloadExcel}
                        >
                          <Dropdown className='w-3 h-2' />
                          <span className='text-white text-[9px] font-bold mr-1'>
                            Excel
                          </span>
                        </Button>
                      </div>
                    </div>
                    <div className='mt-5'>
                      {loadingStates[option] ? (
                        <>
                          <CustomTable
                            data={[]}
                            headerData={selectedPage.headers}
                          />
                          <div className='flex flex-col gap-y-4 p-4'>
                            <Skeleton.Button block={true} active={true} />
                            <Skeleton.Button block={true} active={true} />
                            <Skeleton.Button block={true} active={true} />
                            <Skeleton.Button block={true} active={true} />
                          </div>
                        </>
                      ) : (
                        <CustomTable
                          data={tableData}
                          headerData={selectedPage.headers}
                        />
                      )}
                    </div>
                    {pagination[option]?.totalPages > 1 && (
                      <div className='px-4'>
                        <Pagination
                          className={'mb-4'}
                          current={currentPages[option]}
                          total={pagination[option]?.totalItems}
                          pageSize={pageSize}
                          setCurrentPage={page =>
                            handlePageChange(option, page)
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            )
          })}
        </div>
      </div>
    </div>
  )
}
